import React, { Component } from "react";
import config from 'react-global-configuration';
import { Input, Button } from 'reactstrap';
import jwt_decode from "jwt-decode";
import axios from "axios";

/**
 * Floating "Ask Sparcs AI" button — mount this once inside Admin.jsx
 * (render()), so it shows on every /admin page.
 */
class AskSparcsAI extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showForm: false,   // controls animation (mounted but hidden vs visible)
            rendered: false,   // keeps popup in DOM briefly for exit animation
            name: '',
            email: '',
            submitting: false,
            success: false,
            error: '',
            fabHover: false,
            // Access-whitelist gate (appdb.sparcs_ai_access_whitelist, checked server-side) -
            // the button stays hidden (not just disabled) until we know the answer, so it
            // never flashes on screen for someone who isn't allowed to use it.
            accessChecked: false,
            hasAccess: false
        };
    }

    componentDidMount() {
        const token = localStorage.usertoken;
        if (token) {
            try {
                const decoded = jwt_decode(token);
                this.setState({
                    name: (decoded.firstName || '') + ' ' + (decoded.lastName || ''),
                    email: decoded.email || ''
                });
            } catch (err) {
                console.log("token decode failed in AskSparcsAI");
            }

            axios.post(config.get('API_URL') + '/sparcs-ai/check-access', {}, {
                headers: { Authorization: ` ${token}` }
            }).then(response => {
                this.setState({
                    accessChecked: true,
                    hasAccess: !!(response.data && response.data.has_access)
                });
            }).catch(err => {
                console.log("check-access failed", err);
                // Fail closed - if we can't confirm access, don't show the button.
                this.setState({ accessChecked: true, hasAccess: false });
            });
        } else {
            this.setState({ accessChecked: true, hasAccess: false });
        }
    }

    openForm = () => {
        this.setState({ rendered: true, error: '' });
        // next tick so CSS transition kicks in
        setTimeout(() => this.setState({ showForm: true }), 10);
    }

    closeForm = () => {
        this.setState({ showForm: false });
        // wait for the fade/slide-out transition to finish before unmounting
        setTimeout(() => this.setState({ rendered: false, success: false }), 200);
    }

    toggleForm = () => {
        if (this.state.showForm) {
            this.closeForm();
        } else {
            this.openForm();
        }
    }

    onChange = (e) => {
        this.setState({ [e.target.name]: e.target.value });
    }

    onSubmit = (e) => {
        e.preventDefault();

        if (!this.state.name || !this.state.email) {
            this.setState({ error: "Please enter name and email" });
            return;
        }

        this.setState({ submitting: true, error: '' });

        const token = localStorage.usertoken;
        let object_type = '';
        let object_code = '';
        try {
            const decoded = jwt_decode(token);
            object_type = decoded.object_type;
            object_code = decoded.object_code;
        } catch (err) {
            console.log("token decode failed");
        }

        axios.post(config.get('API_URL') + '/sparcs-ai/save-lead', {
            name: this.state.name,
            email: this.state.email,
            object_type: object_type,
            object_code: object_code
        }, {
            headers: {
                Authorization: ` ${token}`
            }
        }).then(response => {
            // brief success state before opening the new tab - feels intentional, not abrupt
            this.setState({ submitting: false, success: true });
            setTimeout(() => {
                // Same-origin new tab -> localStorage/token already shared, no re-login needed
                window.open('/admin/sparcs-ai-chat', '_blank');
                this.closeForm();
            }, 700);
        }).catch(err => {
            console.log(err);
            this.setState({
                submitting: false,
                error: "Something went wrong. Please try again."
            });
        });
    }

    render() {
        // Don't show the floating button on the chat page itself
        if (window.location.pathname === '/admin/sparcs-ai-chat') {
            return null;
        }

        // Whitelist gate - render nothing (not even a disabled/hidden button) until access is
        // confirmed, and nothing at all if the user's email isn't in the whitelist table.
        if (!this.state.accessChecked || !this.state.hasAccess) {
            return null;
        }

        const { rendered, showForm, fabHover } = this.state;

        return (
            <React.Fragment>
                {rendered &&
                    <div
                        style={{
                            position: 'fixed',
                            bottom: '85px',
                            right: '25px',
                            zIndex: 9999,
                            width: '300px',
                            backgroundColor: '#fff',
                            borderRadius: '10px',
                            boxShadow: '0 5px 25px rgba(0,0,0,0.25)',
                            overflow: 'hidden',
                            opacity: showForm ? 1 : 0,
                            transform: showForm ? 'translateY(0) scale(1)' : 'translateY(15px) scale(0.97)',
                            transformOrigin: 'bottom right',
                            transition: 'opacity 0.2s ease, transform 0.2s ease'
                        }}
                    >
                        <div style={{
                            backgroundColor: '#5e5ce6',
                            color: '#fff',
                            padding: '12px 15px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span style={{ fontWeight: '600' }}>
                                <i className="fa fa-comments" style={{ marginRight: '8px' }}></i>
                                Ask Innoviti Sparcs AI
                            </span>
                            <span
                                onClick={this.closeForm}
                                style={{ cursor: 'pointer', fontSize: '18px', lineHeight: '18px' }}
                            >&times;</span>
                        </div>

                        {this.state.success ?
                            <div style={{ padding: '30px 15px', textAlign: 'center' }}>
                                <div style={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '50%',
                                    backgroundColor: '#e8f8ee',
                                    color: '#2ecc71',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 12px',
                                    fontSize: '20px'
                                }}>
                                    <i className="fa fa-check"></i>
                                </div>
                                <div style={{ fontSize: '14px', color: '#444' }}>Opening your chat...</div>
                            </div>
                            :
                            <div style={{ padding: '15px' }}>
                                <form onSubmit={this.onSubmit}>
                                    <div className="form-group">
                                        <label style={{ fontSize: '13px' }}>Name</label>
                                        <Input
                                            name="name"
                                            type="text"
                                            bsSize="sm"
                                            value={this.state.name}
                                            onChange={this.onChange}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginTop: '10px' }}>
                                        <label style={{ fontSize: '13px' }}>Email</label>
                                        <Input
                                            name="email"
                                            type="email"
                                            bsSize="sm"
                                            value={this.state.email}
                                            onChange={this.onChange}
                                        />
                                    </div>
                                    {this.state.error &&
                                        <div style={{ color: 'red', marginTop: '10px', fontSize: '12px' }}>{this.state.error}</div>
                                    }
                                    <Button
                                        color="primary"
                                        type="submit"
                                        size="sm"
                                        block
                                        style={{ marginTop: '15px' }}
                                        disabled={this.state.submitting}
                                    >
                                        {this.state.submitting ? 'Please wait...' : 'Start Chat'}
                                    </Button>
                                </form>
                            </div>
                        }
                    </div>
                }

                <div
                    onClick={this.toggleForm}
                    onMouseEnter={() => this.setState({ fabHover: true })}
                    onMouseLeave={() => this.setState({ fabHover: false })}
                    className="ask-sparcs-ai-fab"
                    style={{
                        position: 'fixed',
                        bottom: '25px',
                        right: '25px',
                        zIndex: 9999,
                        backgroundColor: '#5e5ce6',
                        color: '#fff',
                        padding: '12px 20px',
                        borderRadius: '30px',
                        boxShadow: fabHover ? '0 6px 18px rgba(94,92,230,0.45)' : '0 4px 12px rgba(0,0,0,0.25)',
                        cursor: 'pointer',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        transform: fabHover ? 'translateY(-2px) scale(1.03)' : 'translateY(0) scale(1)',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                    }}
                >
                    <i className={showForm ? "fa fa-chevron-down" : "fa fa-comments"} style={{ marginRight: '8px' }}></i>
                    Ask Innoviti Sparcs AI
                </div>
            </React.Fragment>
        );
    }
}

export default AskSparcsAI;